// src/pages/registration/pages/invitation-code/invitation-code.tsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentSection, Heading, SubHeading } from '../../shared.styled';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { InstallButton } from '../install/install.styled';
import { EditInput } from '../../../../components/edit-input/edit-input';
import type { OnboardingStepProps } from '../../registration';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { invitationsApi } from '../../../../api';
import {
  AlreadyHaveAccount,
  BottomSection,
  EnterInvitationCode,
  LogInLink,
  LoginSection,
  RequestInvitation,
} from './invitation-code.styled';

export const InvitationCode: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { onboarding, setRegistration, setEmail } = useAuth();
  const navigate = useNavigate();

  // Get invitation code from onboarding state
  const invitationCode = useMemo(() => (onboarding as any)?.invitationCode ?? '', [onboarding]);

  // Controlled input for invitation code
  const [code, setCode] = useState(invitationCode);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // avoid "forever loading" if component unmounts mid-request
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update onboarding when the user types
  useEffect(() => {
    const trimmed = code.trim();
    setRegistration({
      invitationCode: trimmed || undefined,
    } as any);
  }, [code, setRegistration]);

  const safeSetLoading = (v: boolean) => {
    if (isMountedRef.current) setLoading(v);
  };
  const safeSetError = (v: string | null) => {
    if (isMountedRef.current) setErrorMsg(v);
  };

  const handleVerify = async () => {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      safeSetError('Please enter an invitation code');
      return;
    }

    // Basic validation - invitation codes are typically 6-8 characters
    if (trimmedCode.length < 6) {
      safeSetError('Invitation code must be at least 6 characters');
      return;
    }

    safeSetError(null);
    safeSetLoading(true);

    const startTime = Date.now();
    let nextErrorMessage: string | null = null;
    let success = false;
    let data: any = null;

    // IMPORTANT: add a hard timeout so you never spin forever
    // (axios also has a timeout, but we keep a UI fallback too)
    const HARD_UI_TIMEOUT_MS = 35000; // keep above the 30s API timeout
    let hardTimeoutId: number | null = null;

    try {
      hardTimeoutId = window.setTimeout(() => {
        if (!isMountedRef.current) return;
        safeSetLoading(false);
        safeSetError('Network timeout. Please try again.');
      }, HARD_UI_TIMEOUT_MS);

      // Hit backend: /invitations/public/{invitation_code}
      data = await invitationsApi.validateInvitationCode(trimmedCode);

      // If we get data back, the invitation code is valid
      // The endpoint returns 404 for invalid codes, so if we reach here, it's valid
      if (data?.invitation_code) {
        success = true;
      } else {
        nextErrorMessage = `That code doesn't look right. Please check and try again.`;
      }
    } catch (err: any) {
      // Try to show a more useful error if backend responded
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ?? err?.response?.data?.message ?? err?.message ?? null;

      if (status === 404) {
        nextErrorMessage = 'Invitation code not found. Please check and try again.';
      } else if (status === 409) {
        nextErrorMessage = 'This invitation code has already been used.';
      } else if (status === 0 || /network|cors/i.test(String(detail ?? ''))) {
        // common when mixed-content or CORS blocks the browser from sending request
        nextErrorMessage =
          'Network/CORS issue. Check VITE_BACKEND_URL (https vs http) and backend CORS settings.';
      } else if (/timeout/i.test(String(detail ?? ''))) {
        nextErrorMessage = 'Request timed out. Please try again.';
      } else {
        nextErrorMessage = detail ? String(detail) : 'Something went wrong. Please try again.';
      }
    } finally {
      if (hardTimeoutId) window.clearTimeout(hardTimeoutId);

      // Loader should show for at least 2 seconds for better UX
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 2000 - elapsed);

      window.setTimeout(() => {
        // if hard-timeout already fired and stopped loading, don't flip state again
        if (!isMountedRef.current) return;

        if (success) {
          safeSetError(null);
          // Store the validated invitation code AND invitation data
          setRegistration({
            invitationCode: trimmedCode,
            invitationData: data, // Store the full invitation response
            // Pre-fill email if available from invitation
            email: data.email,
            // Pre-fill profile data from invitation
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            company: data.company_name || '',
            nickname: data.nickname || '',
            authProvider: 'email',
            // Resume at the step the user left off (if any)
            ...(data.registration_step ? { registration_step: data.registration_step } : {}),
          } as any);

          // User already created account (step >= 4) but didn't finish registration.
          // Redirect to login so they get a session, then existing routing
          // sends them back to registration at their saved step.
          if (data.registration_step && data.registration_step >= 4 && data.email) {
            setEmail(data.email);
            navigate('/login?s=email-login');
            return;
          }

          goNext();
        } else if (nextErrorMessage) {
          safeSetError(nextErrorMessage);
        }
        safeSetLoading(false);
      }, remaining);
    }
  };

  return (
    <ContentSection data-testid="ob-invitation-code">
      <div data-testid="ob-invitation-code-headings">
        <VSpace $h={32} />
        <Heading data-testid="ob-invitation-code-heading" isLight={false}>
          Judgment. <br />
          Refined.{` `}
        </Heading>
        <VSpace $h={16} />

        <SubHeading data-testid="ob-invitation-code-subheading" isLight={false}>
          The long conversations that create enterprise value.
        </SubHeading>
        {/* <VSpace $h={71} /> */}
      </div>

      <div style={{ width: '100%' }}>
        <EnterInvitationCode>Enter your invitation code</EnterInvitationCode>
        <VSpace $h={16} />

        <EditInput
          placeholder=""
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
        />

        {errorMsg ? (
          <>
            <VSpace $h={12} />
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#ff6b6b' }}>
              {errorMsg}
            </div>
          </>
        ) : null}

        <VSpace $h={16} />
        <InstallButton onClick={handleVerify} disabled={loading}>
          {loading ? 'Verifying...' : 'Verify'}
        </InstallButton>
        <BottomSection>
          <LoginSection>
            <AlreadyHaveAccount>Already Have an Account?</AlreadyHaveAccount>
            <LogInLink
              onClick={() => navigate('/login?s=email-login')}
              style={{ cursor: 'pointer' }}
            >
              Log In
            </LogInLink>
          </LoginSection>
          <RequestInvitation onClick={() => navigate('/login?s=waitlist')}>
            Request an invitation code
          </RequestInvitation>
        </BottomSection>
      </div>
      <div>
      </div>
    </ContentSection>
  );
};

(InvitationCode as any).smallLogo = true;
(InvitationCode as any).progress = false;
(InvitationCode as any).containerAlign = 'flex-start';
(InvitationCode as any).containerJustify = 'flex-start';
(InvitationCode as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
